/** @jsxImportSource preact */
import { h, render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ChatClient, fetchChatStatus, inferApiOrigin, storedGuestEmail } from './client.js';
import { linkify } from './links.js';
import { MAX_FILE_BYTES, isPdf, isSendableFile, pastedFiles, prepareFile } from './pictures.js';
import type { ChatAttachment, ChatMode, ServerFrame, SupportAgentCtx, UiMessage } from './types.js';

// Accent color is themeable via the --sa-accent custom property, set on
// .sa-host from ctx.accentColor (default #111). Bubble side is toggled with
// the .sa-pos-left modifier class.
const STYLES = `
.sa-host { all: initial; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; --sa-accent: #111; }
.sa-bubble {
  position: fixed; bottom: 24px; right: 24px; z-index: 2147483640;
  width: 56px; height: 56px; border-radius: 50%;
  background: var(--sa-accent); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  border: none; font-size: 24px;
}
.sa-bubble:hover { opacity: 0.92; }
.sa-panel {
  position: fixed; bottom: 96px; right: 24px; z-index: 2147483640;
  width: 360px; max-width: calc(100vw - 32px);
  height: 520px; max-height: calc(100vh - 120px);
  background: #fff; border-radius: 12px; overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex; flex-direction: column;
  border: 1px solid rgba(0,0,0,0.08);
}
.sa-pos-left .sa-bubble, .sa-pos-left .sa-panel { right: auto; left: 24px; }
.sa-header { padding: 14px 16px; background: var(--sa-accent); color: #fff; font-weight: 600; display: flex; justify-content: space-between; align-items: center; }
.sa-close { background: transparent; border: none; color: #fff; font-size: 18px; cursor: pointer; padding: 0 4px; }
.sa-header-actions { display: flex; align-items: center; gap: 6px; }
.sa-restart { background: rgba(255,255,255,0.18); border: none; color: #fff; font-size: 12px; font-weight: 600; border-radius: 6px; padding: 4px 8px; cursor: pointer; }
.sa-restart:hover { background: rgba(255,255,255,0.3); }
.sa-restart:disabled { opacity: 0.6; cursor: default; }
.sa-body { flex: 1; overflow-y: auto; padding: 12px 16px; background: #f7f7f8; }
.sa-intro { font-size: 13px; color: #666; text-align: center; padding: 12px 8px; }
.sa-msg { margin: 8px 0; max-width: 80%; padding: 8px 12px; border-radius: 12px; font-size: 14px; line-height: 1.4; word-wrap: break-word; white-space: pre-wrap; }
.sa-msg-in { background: #fff; align-self: flex-start; border: 1px solid rgba(0,0,0,0.06); }
.sa-msg-out { background: var(--sa-accent); color: #fff; margin-left: auto; }
.sa-msg-pending { opacity: 0.6; }
.sa-url { color: inherit; text-decoration: underline; text-underline-offset: 2px; overflow-wrap: anywhere; cursor: pointer; }
/* Our replies: links in the shop's colour, bold, with an arrow — plainly a link to tap. */
.sa-msg-in .sa-url { color: var(--sa-accent); font-weight: 600; text-decoration-thickness: 1.5px; }
.sa-msg-in .sa-url::after { content: ' ↗'; font-weight: 400; text-decoration: none; display: inline-block; }
.sa-url:hover { opacity: 0.8; }
.sa-row { display: flex; flex-direction: column; }
.sa-typing { font-size: 12px; color: #888; padding: 4px 0 0; height: 16px; }
.sa-input-row { display: flex; padding: 8px; border-top: 1px solid rgba(0,0,0,0.08); background: #fff; }
.sa-input { flex: 1; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 8px 10px; font-size: 14px; resize: none; font-family: inherit; }
.sa-input:focus { outline: 2px solid var(--sa-accent); outline-offset: -1px; }
.sa-send { margin-left: 6px; background: var(--sa-accent); color: #fff; border: none; border-radius: 8px; padding: 0 14px; cursor: pointer; font-size: 14px; }
.sa-send:disabled { background: #888; cursor: not-allowed; }
.sa-gate { padding: 16px; display: flex; flex-direction: column; gap: 10px; background: #fff; flex: 1; }
.sa-gate label { font-size: 13px; font-weight: 600; }
.sa-gate input { border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; padding: 8px 10px; font-size: 14px; }
.sa-gate button { background: var(--sa-accent); color: #fff; border: none; border-radius: 8px; padding: 10px; cursor: pointer; font-size: 14px; }
.sa-error { color: #c00; font-size: 12px; padding: 4px 16px; }
.sa-status { display: flex; gap: 8px; align-items: flex-start; font-size: 12px; line-height: 1.4; padding: 8px 16px; background: #f3f4f6; color: #374151; border-bottom: 1px solid rgba(0,0,0,0.06); }
.sa-status-dot { flex: none; width: 8px; height: 8px; border-radius: 50%; margin-top: 4px; background: #9ca3af; }
.sa-status-dot.on { background: #16a34a; }
.sa-privacy { color: inherit; text-decoration: underline; white-space: nowrap; }
.sa-author { font-size: 11px; color: #6b7280; margin: 8px 0 -4px 2px; }
.sa-handoff { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 12px; padding: 6px 16px; background: #fff; border-bottom: 1px solid rgba(0,0,0,0.06); color: #374151; }
.sa-handoff button { background: none; border: 1px solid var(--sa-accent); color: var(--sa-accent); border-radius: 999px; padding: 4px 10px; font-size: 12px; cursor: pointer; white-space: nowrap; }
.sa-handoff button:disabled { opacity: 0.5; cursor: default; }
.sa-verify { font-size: 12px; line-height: 1.4; padding: 8px 16px; background: #fffbeb; color: #78350f; border-bottom: 1px solid rgba(0,0,0,0.06); }
.sa-verify.ok { background: #f0fdf4; color: #166534; }
.sa-verify form { display: flex; gap: 6px; margin-top: 6px; }
.sa-verify input { flex: 1; min-width: 0; border: 1px solid rgba(0,0,0,0.15); border-radius: 6px; padding: 6px 8px; font-size: 13px; font-family: inherit; }
.sa-verify button { background: var(--sa-accent); color: #fff; border: none; border-radius: 6px; padding: 6px 10px; font-size: 13px; cursor: pointer; }
.sa-verify button:disabled { opacity: 0.6; cursor: wait; }
.sa-link { background: none !important; color: inherit !important; padding: 0 !important; text-decoration: underline; font-size: 12px !important; cursor: pointer; }
.sa-verify-note { margin-top: 4px; }
.sa-att-img { display: block; max-width: 100%; max-height: 240px; border-radius: 8px; margin-top: 6px; background: rgba(0,0,0,0.04); }
.sa-msg-media { padding: 4px; }
.sa-msg-media .sa-att-img { margin-top: 0; }
.sa-att { display: block; margin-top: 6px; color: inherit; font-size: 13px; text-decoration: underline; word-break: break-all; }
.sa-attach { flex: none; width: 38px; margin-right: 6px; background: #fff; border: 1px solid rgba(0,0,0,0.12); border-radius: 8px; cursor: pointer; color: #444; display: flex; align-items: center; justify-content: center; padding: 0; }
.sa-attach:hover { border-color: var(--sa-accent); color: var(--sa-accent); }
.sa-attach:disabled { opacity: 0.5; cursor: default; }
.sa-pics { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 8px 0; background: #fff; border-top: 1px solid rgba(0,0,0,0.08); }
.sa-pics + .sa-input-row { border-top: none; }
.sa-pic { position: relative; width: 52px; height: 52px; border-radius: 8px; overflow: hidden; background: #e5e7eb; display: flex; align-items: center; justify-content: center; font-size: 18px; }
.sa-pic img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.sa-pic-name { position: absolute; left: 3px; right: 3px; bottom: 2px; font-size: 9px; line-height: 1.2; color: #374151; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; text-align: center; }
.sa-pic button { position: absolute; top: 2px; right: 2px; width: 18px; height: 18px; border-radius: 50%; border: none; background: rgba(0,0,0,0.65); color: #fff; font-size: 12px; line-height: 18px; padding: 0; cursor: pointer; }
.sa-panel.sa-drop { outline: 2px dashed var(--sa-accent); outline-offset: -6px; }
`;

/** A chat file's link, absolute (the server sends it relative to the API). */
function withAbsoluteUrls(ctx: SupportAgentCtx, files: ChatAttachment[] | undefined): ChatAttachment[] | undefined {
  if (!files?.length) return undefined;
  const origin = ctx.apiOrigin ?? inferApiOrigin();
  return files.map((f) => ({ ...f, url: /^https?:/.test(f.url) ? f.url : `${origin}${f.url}` }));
}

function fileSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

const isPicture = (f: ChatAttachment) => /^image\/(png|jpe?g|gif|webp)$/i.test(f.mimeType);

/** Pictures in the bubble (first), every other file as a link to download. */
function Attachments({ files, onLoad }: { files: ChatAttachment[]; onLoad: () => void }) {
  const ordered = [...files.filter(isPicture), ...files.filter((f) => !isPicture(f))];
  return (
    <>
      {ordered.map((f) =>
        isPicture(f) ? (
          <a key={f.url} href={f.url} target="_blank" rel="noopener noreferrer" title={f.filename}>
            <img class="sa-att-img" src={f.url} alt={f.filename} onLoad={onLoad} />
          </a>
        ) : (
          <a key={f.url} class="sa-att" href={f.url} target="_blank" rel="noopener noreferrer" download={f.filename}>
            📎 {f.filename} ({fileSize(f.sizeBytes)})
          </a>
        ),
      )}
    </>
  );
}

type VerifyStep = 'closed' | 'email' | 'code';

const VERIFY_ERRORS: Record<string, string> = {
  expired: 'Koden er utløpt. Be om en ny kode.',
  too_many_attempts: 'For mange forsøk. Vent litt, og be om en ny kode.',
  too_many_codes: 'Du har bedt om flere koder. Vent noen minutter og prøv igjen.',
  no_code: 'Be om en kode først.',
  email_required: 'Skriv inn en gyldig e-postadresse.',
  invalid_request: 'Skriv inn en gyldig e-postadresse.',
  invalid_code_format: 'Koden er 6 siffer.',
  send_failed: 'Vi fikk ikke sendt koden akkurat nå. Prøv igjen om litt.',
  no_mailbox: 'Vi fikk ikke sendt koden akkurat nå. Prøv igjen om litt.',
  invalid_session: 'Chatten er utløpt. Last inn siden på nytt.',
  network: 'Fikk ikke kontakt. Prøv igjen.',
};

interface AppProps {
  ctx: SupportAgentCtx;
}

function App({ ctx }: AppProps) {
  const [open, setOpen] = useState(false);
  // Staff online / AI assistant? Checked when the panel opens and every minute.
  const [online, setOnline] = useState<boolean | null>(null);
  const [closedHours, setClosedHours] = useState<string | null>(null);
  const [aiAssistant, setAiAssistant] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>('ai');
  const [handoffAsked, setHandoffAsked] = useState(false);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const check = () => {
      void fetchChatStatus(ctx).then((v) => {
        if (!alive || !v) return;
        setOnline(v.online);
        setClosedHours(v.hours);
        setAiAssistant(v.ai);
      });
    };
    check();
    const t = window.setInterval(check, 60_000);
    return () => { alive = false; window.clearInterval(t); };
  }, [open]);
  // A logged-in customer can skip the email step only through the App Proxy,
  // where Shopify vouches for them; the direct handshake needs an email.
  // A guest who already started a chat in this browser keeps it on every page.
  if (!ctx.customerEmail && !ctx.customerId) {
    const remembered = storedGuestEmail(ctx.shop);
    if (remembered) ctx.customerEmail = remembered;
  }
  const [needsEmail, setNeedsEmail] = useState(!ctx.customerEmail && !(ctx.proxyPath && ctx.customerId));
  const [emailInput, setEmailInput] = useState('');
  const [draft, setDraft] = useState('');
  /** Pictures and PDFs waiting under the text box until the customer presses Send. */
  const [pictures, setPictures] = useState<Array<{ key: string; file: File; previewUrl: string }>>([]);
  const [dropping, setDropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [agentTyping, setAgentTyping] = useState(false);
  // "Skriver…" never outlives a reply that didn't come: off after 30 s.
  useEffect(() => {
    if (!agentTyping) return;
    const t = window.setTimeout(() => setAgentTyping(false), 30_000);
    return () => window.clearTimeout(t);
  }, [agentTyping]);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  // Email verification: only needed for order questions.
  const [verified, setVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifyStep, setVerifyStep] = useState<VerifyStep>('closed');
  const [verifyEmailInput, setVerifyEmailInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [verifyNote, setVerifyNote] = useState<string | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const clientRef = useRef<ChatClient | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<number | null>(null);

  // Initialise client lazily on first open + identifier present.
  useEffect(() => {
    if (!open) return;
    if (needsEmail) return;
    if (clientRef.current) return;
    const client = new ChatClient(ctx);
    clientRef.current = client;
    const off = client.on(handleFrame);
    client.start().then(() => setStarted(true)).catch((err) => {
      setError(
        String(err?.message).endsWith('_429')
          ? 'Det er startet mange chatter herfra den siste timen. Prøv igjen litt senere.'
          : 'Fikk ikke kontakt med chatten. Prøv igjen om litt.',
      );
    });
    return () => {
      off();
    };
  }, [open, needsEmail]);

  useEffect(() => {
    return () => {
      clientRef.current?.destroy();
    };
  }, []);

  // "Ny samtale": leave this conversation and start a fresh one (same email).
  async function restartConversation() {
    const client = clientRef.current;
    if (!client || restarting) return;
    setRestarting(true);
    try {
      await client.restart();
      setMessages([]);
      setDraft('');
      setAgentTyping(false);
      setError(null);
      setChatMode('ai');
      setHandoffAsked(false);
      // Email verification belongs to a session — the new one starts unverified.
      setVerified(false);
      setVerifiedEmail(null);
      setVerifyStep('closed');
      setVerifyNote(null);
    } catch (err) {
      setError(`Kunne ikke starte en ny samtale: ${err instanceof Error ? err.message : 'ukjent feil'}`);
    } finally {
      setRestarting(false);
    }
  }

  // Auto-scroll on new message.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages.length, agentTyping]);
  // A picture that finishes loading grows its bubble: stay at the bottom.
  function keepAtBottom() {
    const el = bodyRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 320) el.scrollTop = el.scrollHeight;
  }

  function handleFrame(frame: ServerFrame) {
    if (frame.type === 'welcome') {
      // A "stopped typing" lost with the old socket must not leave "Skriver…".
      setAgentTyping(false);
      setVerified(frame.identityVerified === true);
      setVerifiedEmail(frame.verifiedEmail ?? null);
      if (frame.aiAssistant !== undefined) setAiAssistant(frame.aiAssistant);
      if (frame.chatMode) setChatMode(frame.chatMode);
      // After a reconnect: what the customer sent that the server hasn't
      // stored yet stays on screen, still pending — the client sends it now.
      const stored = new Set(frame.history.map((m) => m.externalMessageId).filter(Boolean));
      setMessages((prev) => [
        ...frame.history.map((m) => ({
          id: m.id,
          direction: m.direction,
          body: m.body,
          receivedAt: m.receivedAt,
          ...(m.author ? { author: m.author } : {}),
          ...(m.attachments?.length ? { attachments: withAbsoluteUrls(ctx, m.attachments) } : {}),
        })),
        ...prev.filter((m) => m.pending && !stored.has(m.id)),
      ]);
      return;
    }
    if (frame.type === 'mode') {
      setChatMode(frame.mode);
      if (frame.mode === 'ai') setHandoffAsked(false);
      return;
    }
    if (frame.type === 'message') {
      setMessages((prev) => {
        if (prev.some((m) => m.id === frame.messageId)) return prev;
        // The server echoes the customer's own message back as a receipt:
        // confirm the pending copy rather than adding a second bubble.
        const pendingIdx = frame.externalMessageId
          ? prev.findIndex((m) => m.id === frame.externalMessageId)
          : -1;
        if (pendingIdx >= 0) {
          const next = prev.slice();
          next[pendingIdx] = {
            ...next[pendingIdx]!,
            id: frame.messageId,
            receivedAt: frame.receivedAt,
            pending: false,
            ...(frame.attachments?.length ? { attachments: withAbsoluteUrls(ctx, frame.attachments) } : {}),
          };
          return next;
        }
        return [
          ...prev,
          {
            id: frame.messageId,
            direction: frame.direction,
            body: frame.body,
            receivedAt: frame.receivedAt,
            ...(frame.author ? { author: frame.author } : {}),
            ...(frame.attachments?.length ? { attachments: withAbsoluteUrls(ctx, frame.attachments) } : {}),
          },
        ];
      });
      return;
    }
    if (frame.type === 'typing' && frame.who === 'agent') {
      setAgentTyping(frame.isTyping);
      return;
    }
    if (frame.type === 'error') {
      setError(
        frame.code === 'rate_limited'
          ? frame.message
          : frame.code === 'session_invalid'
            ? 'Chatten er utløpt. Last inn siden på nytt for å starte en ny.'
            : 'Noe gikk galt i chatten. Prøv igjen om litt.',
      );
    }
  }

  function submitEmail(e: Event) {
    e.preventDefault();
    if (!emailInput || !/.+@.+\..+/.test(emailInput)) return;
    // Mutate ctx so the client picks it up on start().
    ctx.customerEmail = emailInput.trim();
    setNeedsEmail(false);
  }

  function openVerify() {
    setVerifyEmailInput(ctx.customerEmail ?? '');
    setVerifyNote(null);
    setVerifyStep('email');
  }

  async function requestCode(e?: Event) {
    e?.preventDefault();
    const c = clientRef.current;
    const email = verifyEmailInput.trim();
    if (!c || verifyBusy) return;
    if (!/.+@.+\..+/.test(email)) {
      setVerifyNote(VERIFY_ERRORS.email_required!);
      return;
    }
    setVerifyBusy(true);
    const r = await c.verifyStart(email);
    setVerifyBusy(false);
    if (!r.ok) {
      setVerifyNote(VERIFY_ERRORS[r.error] ?? VERIFY_ERRORS.send_failed!);
      return;
    }
    setCodeInput('');
    setVerifyStep('code');
    setVerifyNote(
      r.testMode
        ? `Testmodus: koden ble ikke sendt på e-post (den står i serverloggen).`
        : `Vi har sendt en kode til ${r.sentTo}. Den gjelder i 10 minutter.`,
    );
  }

  async function checkCode(e: Event) {
    e.preventDefault();
    const c = clientRef.current;
    const code = codeInput.replace(/\s/g, '');
    if (!c || verifyBusy) return;
    if (!/^\d{6}$/.test(code)) {
      setVerifyNote(VERIFY_ERRORS.invalid_code_format!);
      return;
    }
    setVerifyBusy(true);
    const r = await c.verifyCheck(code);
    setVerifyBusy(false);
    if (r.ok) {
      setVerified(true);
      setVerifiedEmail(r.email);
      setVerifyStep('closed');
      setVerifyNote(null);
      return;
    }
    setVerifyNote(
      r.error === 'wrong_code'
        ? `Feil kode.${r.attemptsLeft !== undefined ? ` Du har ${r.attemptsLeft} forsøk igjen.` : ''}`
        : VERIFY_ERRORS[r.error] ?? VERIFY_ERRORS.network!,
    );
  }

  function send() {
    const text = draft.trim();
    if ((!text && !pictures.length) || !clientRef.current) return;
    if (text) {
      const id = clientRef.current.send(text);
      // Directions follow the server: INBOUND = from the customer, OUTBOUND = from us.
      setMessages((prev) => [
        ...prev,
        { id, direction: 'INBOUND', body: text, receivedAt: new Date().toISOString(), pending: true },
      ]);
      setDraft('');
      clientRef.current.setTyping(false);
    }
    // Each file is a message of its own, after the text.
    for (const p of pictures) void sendPicture(p.file, p.previewUrl);
    setPictures([]);
  }

  function addPictures(list: File[]) {
    const pics = list.filter(isSendableFile);
    if (pics.length < list.length) setError('Du kan sende bilder og PDF-er.');
    else setError(null);
    setPictures((prev) => [
      ...prev,
      ...pics.slice(0, Math.max(0, 5 - prev.length)).map((file, i) => ({ key: `${Date.now()}-${i}`, file, previewUrl: URL.createObjectURL(file) })),
    ]);
  }

  function removePicture(key: string) {
    setPictures((prev) => {
      const p = prev.find((x) => x.key === key);
      if (p) URL.revokeObjectURL(p.previewUrl);
      return prev.filter((x) => x.key !== key);
    });
  }

  /** Show it right away (pending), upload, then confirm with the server's link — or take it back with an error. */
  async function sendPicture(file: File, previewUrl: string) {
    const c = clientRef.current;
    if (!c) return;
    const { blob, name } = await prepareFile(file);
    if (blob.size > MAX_FILE_BYTES) {
      setError('Filen er for stor (høyst 10 MB).');
      URL.revokeObjectURL(previewUrl);
      return;
    }
    const { externalMessageId, upload } = c.sendPicture(blob, name);
    setMessages((prev) => [
      ...prev,
      {
        id: externalMessageId,
        direction: 'INBOUND',
        body: '',
        receivedAt: new Date().toISOString(),
        pending: true,
        attachments: [{ url: previewUrl, filename: name, mimeType: blob.type || 'image/jpeg', sizeBytes: blob.size }],
      },
    ]);
    try {
      const sent = await upload;
      // The socket's echo may have confirmed it already.
      setMessages((prev) => prev.map((m) => (m.id === externalMessageId
        ? { ...m, id: sent.messageId, pending: false, ...(sent.attachments.length ? { attachments: withAbsoluteUrls(ctx, sent.attachments) } : {}) }
        : m)));
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== externalMessageId));
      const code = err instanceof Error ? err.message : '';
      setError(
        code === 'too_many_pictures' ? 'Du har sendt mange bilder. Vent litt og prøv igjen.'
          : code === 'unsupported_file' || code === 'not_a_picture' ? 'Du kan sende bilder (JPG, PNG, HEIC …) og PDF-er.'
            : code === 'too_large' ? 'Filen er for stor (høyst 10 MB).'
              : 'Kunne ikke sende filen. Prøv igjen.',
      );
    }
    // The bubble shows the server's copy once confirmed (or nothing on error).
    window.setTimeout(() => URL.revokeObjectURL(previewUrl), 30_000);
  }

  function onDraftChange(value: string) {
    setDraft(value);
    const c = clientRef.current;
    if (!c) return;
    if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
    if (value.length > 0) {
      c.setTyping(true);
      typingTimerRef.current = window.setTimeout(() => c.setTyping(false), 2000) as unknown as number;
    } else {
      c.setTyping(false);
    }
  }

  const accent = ctx.accentColor || '#111';
  // GDPR: what we do with what they write — the shop's privacy policy.
  const privacyUrl = ctx.privacyUrl || `${window.location.origin}/policies/privacy-policy`;
  const hostClass = `sa-host${ctx.position === 'left' ? ' sa-pos-left' : ''}`;
  const title = ctx.title || 'Chat med oss';

  return (
    <div class={hostClass} style={`--sa-accent:${accent}`}>
      <style>{STYLES}</style>
      {!open && (
        <button
          class="sa-bubble"
          aria-label="Open chat"
          onClick={() => setOpen(true)}
        >
          {/* simple chat icon */}
          💬
        </button>
      )}
      {open && (
        <div
          class={`sa-panel${dropping ? ' sa-drop' : ''}`}
          role="dialog"
          aria-label="Customer support chat"
          // Pictures dropped on the chat wait under the text box like picked ones.
          onDragOver={(e) => { if (started && e.dataTransfer?.types.includes('Files')) { e.preventDefault(); setDropping(true); } }}
          onDragLeave={() => setDropping(false)}
          onDrop={(e) => {
            setDropping(false);
            if (!started || !e.dataTransfer?.files.length) return;
            e.preventDefault();
            addPictures(Array.from(e.dataTransfer.files));
          }}
        >
          <div class="sa-header">
            <span>{title}</span>
            <span class="sa-header-actions">
              {started && messages.length > 0 && (
                <button class="sa-restart" disabled={restarting} onClick={restartConversation} title="Avslutt denne samtalen og start på nytt">
                  {restarting ? 'Starter…' : 'Ny samtale'}
                </button>
              )}
              <button class="sa-close" aria-label="Lukk" onClick={() => setOpen(false)}>
                ×
              </button>
            </span>
          </div>
          {online !== null && (
            <div class="sa-status">
              <span class={`sa-status-dot${online ? ' on' : ''}`} />
              <span>
                {aiAssistant
                  ? online
                    ? 'AI-assistenten vår svarer med en gang. Selgerne er også pålogget hvis du vil prate med en av dem.'
                    : closedHours
                      ? `AI-assistenten vår svarer med en gang. Selgerne er i chatten ${closedHours} — trenger du dem, følger vi opp på e-post.`
                      : 'AI-assistenten vår svarer med en gang. Selgerne er ikke pålogget nå — trenger du dem, følger vi opp på e-post.'
                  : online
                    ? 'Vi er pålogget og svarer vanligvis i løpet av noen minutter.'
                    : closedHours
                      ? `Vi er i chatten ${closedHours}. Skriv likevel – er du ikke her når vi svarer, får du svaret på e-post.`
                      : 'Vi er ikke pålogget akkurat nå. Skriv likevel – er du ikke her når vi svarer, får du svaret på e-post.'}
                {' '}<a class="sa-privacy" href={privacyUrl} target="_blank" rel="noopener noreferrer">Personvern</a>
              </span>
            </div>
          )}
          {!needsEmail && started && aiAssistant && (
            <div class="sa-handoff">
              <span>
                {chatMode === 'waiting'
                  ? 'Venter på en selger …'
                  : chatMode === 'human'
                    ? 'Du chatter med en av våre ansatte.'
                    : 'Du chatter med AI-assistenten.'}
              </span>
              {chatMode === 'ai' && (
                <button
                  disabled={handoffAsked}
                  onClick={() => {
                    if (clientRef.current?.requestHandoff()) setHandoffAsked(true);
                  }}
                >
                  Snakk med en ansatt
                </button>
              )}
            </div>
          )}
          {!needsEmail && started && (
            verified ? (
              <div class="sa-verify ok">
                ✓ E-posten din er bekreftet{verifiedEmail ? ` (${verifiedEmail})` : ''} – vi kan hjelpe deg med bestillinger.
              </div>
            ) : verifyStep === 'closed' ? (
              <div class="sa-verify">
                🔒 Gjelder det en bestilling?{' '}
                <button class="sa-link" onClick={openVerify}>Bekreft e-posten din</button>, så kan vi se på den.
              </div>
            ) : verifyStep === 'email' ? (
              <div class="sa-verify">
                Vi sender en kode til e-posten du handlet med.
                <form onSubmit={requestCode}>
                  <input
                    type="email"
                    aria-label="E-post"
                    placeholder="deg@eksempel.no"
                    value={verifyEmailInput}
                    onInput={(e) => setVerifyEmailInput((e.target as HTMLInputElement).value)}
                  />
                  <button type="submit" disabled={verifyBusy}>Send kode</button>
                </form>
                {verifyNote && <div class="sa-verify-note">{verifyNote}</div>}
                <button class="sa-link" onClick={() => setVerifyStep('closed')}>Avbryt</button>
              </div>
            ) : (
              <div class="sa-verify">
                {verifyNote}
                <form onSubmit={checkCode}>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    aria-label="Kode"
                    placeholder="6 siffer"
                    value={codeInput}
                    onInput={(e) => setCodeInput((e.target as HTMLInputElement).value)}
                  />
                  <button type="submit" disabled={verifyBusy}>Bekreft</button>
                </form>
                <button class="sa-link" onClick={() => void requestCode()}>Send ny kode</button>
                {' · '}
                <button class="sa-link" onClick={() => setVerifyStep('closed')}>Avbryt</button>
              </div>
            )
          )}
          {needsEmail ? (
            <form class="sa-gate" onSubmit={submitEmail}>
              <label for="sa-email">Din e-post</label>
              <input
                id="sa-email"
                type="email"
                required
                placeholder="deg@eksempel.no"
                value={emailInput}
                onInput={(e) => setEmailInput((e.target as HTMLInputElement).value)}
              />
              <p style="font-size:12px;color:#666;margin:0;">
                Vi bruker den til å svare deg hvis samtalen blir avbrutt. Les mer i{' '}
                <a class="sa-privacy" href={privacyUrl} target="_blank" rel="noopener noreferrer">personvernerklæringen</a>.
              </p>
              <button type="submit">Start chat</button>
            </form>
          ) : (
            <>
              <div class="sa-body" ref={bodyRef}>
                {messages.length === 0 && ctx.greeting && (
                  <div class="sa-intro">{ctx.greeting}</div>
                )}
                {messages.map((m, i) => (
                  <div class="sa-row" key={m.id}>
                    {m.direction === 'OUTBOUND' && m.author && messages[i - 1]?.author?.kind !== m.author.kind && (
                      <div class="sa-author">{m.author.kind === 'ai' ? 'AI-assistent' : m.author.name ?? title}</div>
                    )}
                    <div
                      // The customer's own messages (INBOUND to us) sit on the right.
                      class={`sa-msg ${m.direction === 'INBOUND' ? 'sa-msg-out' : 'sa-msg-in'} ${
                        m.pending ? 'sa-msg-pending' : ''
                      } ${!m.body && m.attachments?.length && m.attachments.every(isPicture) ? 'sa-msg-media' : ''}`}
                    >
                      {linkify(m.body.trim())}
                      {m.attachments?.length ? <Attachments files={m.attachments} onLoad={keepAtBottom} /> : null}
                    </div>
                  </div>
                ))}
                <div class="sa-typing">{agentTyping ? 'Skriver…' : ''}</div>
              </div>
              {error && <div class="sa-error">{error}</div>}
              {pictures.length > 0 && (
                <div class="sa-pics">
                  {pictures.map((p) => (
                    <div class="sa-pic" key={p.key} title={p.file.name}>
                      {isPdf(p.file) ? (
                        <>📄<span class="sa-pic-name">{p.file.name}</span></>
                      ) : (
                        <>📷<img src={p.previewUrl} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /></>
                      )}
                      <button type="button" aria-label={`Fjern ${p.file.name}`} onClick={() => removePicture(p.key)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <div class="sa-input-row">
                <button
                  type="button"
                  class="sa-attach"
                  title="Send bilde eller PDF"
                  aria-label="Send bilde eller PDF"
                  disabled={!started}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  multiple
                  style="display:none"
                  onChange={(e) => {
                    const input = e.currentTarget as HTMLInputElement;
                    addPictures(Array.from(input.files ?? []));
                    input.value = '';
                  }}
                />
                <textarea
                  class="sa-input"
                  rows={1}
                  placeholder="Skriv en melding…"
                  value={draft}
                  onInput={(e) => onDraftChange((e.target as HTMLTextAreaElement).value)}
                  onPaste={(e) => {
                    const pics = pastedFiles(e as unknown as ClipboardEvent);
                    if (pics) { e.preventDefault(); addPictures(pics); }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  disabled={!started}
                />
                <button class="sa-send" onClick={send} disabled={!started || (draft.trim().length === 0 && pictures.length === 0)}>
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function mount(ctx: SupportAgentCtx) {
  const host = document.createElement('div');
  host.id = 'support-agent-widget-root';
  // Don't let the page see anything inside.
  host.style.cssText = 'all: initial; position: fixed; z-index: 2147483640;';
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'open' });
  const inner = document.createElement('div');
  shadow.appendChild(inner);
  render(<App ctx={ctx} />, inner);
}
